<template>
  <a :href="aemLink && aemLink.toString()" :title="title" @click="handleLinkClick">
    <slot />
  </a>
</template>

<script lang="ts">
import { navigateTo, resolve } from '@seamless/router';
import { computed, defineComponent, PropType, ref, watch } from 'vue';
import { useDealerContext } from '../../../composables/dealer-context.composable';
import { useRoute, useRouter } from '../../../composables/router.composable';
import SeamlessRouterImpl from '../../../router/seamless-router-impl';
import { SeamlessRouteConfig } from '../../../router/types';
import { adjustPDPUrl, isMockServer } from '../../../utils/aem.util';

export default defineComponent({
  name: 'EmhRouterLink',

  props: {
    to: {
      type: Object as PropType<{
        name: string;
        params?: Record<string, string>;
        query?: Record<string, string>;
      }>,
      required: true,
    },
    title: {
      type: String,
      default: undefined,
    },
  },

  setup(props) {
    const router = useRouter();
    const route = useRoute();
    const { dealerContext, addDealerContextParamsToUrl } = useDealerContext();
    const targetRoute = computed(() => router.findRouteConfig(props.to.name));

    const aemLink = ref<URL>();

    const updateLink = () => {
      if (!targetRoute.value) return;

      let base = (targetRoute.value as SeamlessRouteConfig).link;
      const namespace = (router as SeamlessRouterImpl).namespace;

      let url: URL | null;

      // Build a URL on our own it case of it's abosolut
      if (base?.startsWith('http')) {
        url = new URL(base);

        // Add all search params to it if they are provided
        Object.entries(props.to.query || props.to.params || {}).forEach(([key, value]) => {
          url?.searchParams.set(namespace + key, value);
        });
      } else {
        // Workaround for Seamless router bug: https://git.i.mercedes-benz.com/dh-io-mbmxp/seamless/issues/1402
        if (!base && isMockServer()) {
          // Using "clear" in the resolve call below will also clear the pathname, resulting in
          // a wrong URL. We need to provide the current pathname as base to get the correct URL.
          base = window.location.pathname;
        }

        url = resolve(
          {
            namespace,
            pathname: targetRoute.value.path,
            searchParams: props.to.query || props.to.params,
          },
          { base, clear: true },
        );
      }

      if (url) {
        addDealerContextParamsToUrl(url);

        // In case of a navigation to PDP a URL should be adjusted to support pathname props
        aemLink.value = props.to.name === 'pdp' ? adjustPDPUrl(url, namespace) : url;
      }
    };

    watch([route, dealerContext], updateLink, { immediate: true });

    const handleLinkClick = (event: MouseEvent) => {
      event.preventDefault();

      if (aemLink.value) {
        if (aemLink.value.host !== window.location.host) {
          window.location.href = aemLink.value.toString();
        } else {
          navigateTo(aemLink.value);
        }
      }
    };

    return { aemLink, handleLinkClick };
  },
});
</script>
